import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Chip,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";

const FONT = "0.8rem";
const HEADER_FONT = "0.75rem";

const fmtAmt = (v) => (v == null ? "—" : `$${Math.round(v).toLocaleString()}`);

const hSx = (align = "right") => ({
  fontSize: HEADER_FONT,
  fontWeight: 700,
  align,
  whiteSpace: "nowrap",
});

const fmtBandStart = (m) => `$${(m * 1_000_000).toLocaleString()}`;

const BandRow = ({ band }) => (
  <TableRow hover>
    <TableCell sx={{ fontSize: FONT }}>
      {fmtBandStart(band.attachment_millions)}
    </TableCell>
    <TableCell sx={{ fontSize: FONT }} align={"right"}>
      {fmtAmt(band.combined)}
    </TableCell>
    <TableCell sx={{ fontSize: FONT, color: "text.secondary" }} align={"right"}>
      {fmtAmt(band.min)}
    </TableCell>
    <TableCell
      sx={{
        fontSize: FONT,
        color: band.min_binding ? "warning.main" : "text.secondary",
      }}
      align={"right"}
    >
      {band.min_binding ? (
        <Chip
          label={"TRUE"}
          size={"small"}
          color={"warning"}
          sx={{ fontSize: "0.65rem", height: 16 }}
        />
      ) : (
        "FALSE"
      )}
    </TableCell>
    <TableCell
      sx={{ fontSize: FONT, fontWeight: band.min_binding ? 700 : 400 }}
      align={"right"}
    >
      {fmtAmt(band.final)}
    </TableCell>
    <TableCell sx={{ fontSize: FONT, color: "text.secondary" }} align={"right"}>
      {fmtAmt(band.min_uplift || 0)}
    </TableCell>
  </TableRow>
);

const WhatIfRow = ({
  label,
  prem_ops,
  pco,
  liquor,
  auto,
  combined,
  bold,
  highlight,
}) => (
  <TableRow
    hover={!highlight}
    sx={highlight ? { backgroundColor: "grey.50" } : {}}
  >
    <TableCell sx={{ fontSize: FONT, fontWeight: bold ? 700 : 400 }}>
      {label}
    </TableCell>
    <TableCell
      sx={{ fontSize: FONT, fontWeight: bold ? 700 : 400 }}
      align={"right"}
    >
      {fmtAmt(prem_ops)}
    </TableCell>
    <TableCell
      sx={{ fontSize: FONT, fontWeight: bold ? 700 : 400 }}
      align={"right"}
    >
      {fmtAmt(pco)}
    </TableCell>
    <TableCell
      sx={{ fontSize: FONT, fontWeight: bold ? 700 : 400 }}
      align={"right"}
    >
      {fmtAmt(liquor)}
    </TableCell>
    <TableCell
      sx={{ fontSize: FONT, fontWeight: bold ? 700 : 400 }}
      align={"right"}
    >
      {fmtAmt(auto)}
    </TableCell>
    <TableCell
      sx={{ fontSize: FONT, fontWeight: bold ? 700 : 400 }}
      align={"right"}
    >
      {fmtAmt(combined)}
    </TableCell>
  </TableRow>
);

const SectionHeader = ({ label, open, onToggle }) => (
  <Stack
    direction={"row"}
    alignItems={"center"}
    justifyContent={"space-between"}
    onClick={onToggle}
    sx={{
      cursor: "pointer",
      px: 1.5,
      py: 1,
      backgroundColor: "grey.100",
      borderRadius: 1,
    }}
  >
    <Typography fontSize={"0.8rem"} fontWeight={700} color={"text.secondary"}>
      {label}
    </Typography>
    {open ? (
      <ExpandLessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
    ) : (
      <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
    )}
  </Stack>
);

const XsRatingDetails = ({ values }) => {
  const [bandOpen, setBandOpen] = useState(true);
  const [whatIfOpen, setWhatIfOpen] = useState(true);

  if (!values) return null;

  const { band_details, band_details_technical, totals, what_if } = values;

  // Rated Layer Bands (Excel "Rated Layer Premium" table) uses GUIDELINE-tier
  // bands — i.e. sums to FSES Technical. What If 1xN x P rows below use the
  // OVERRIDE-tier bands (sums to FSES Guideline) — that's `band_details`.
  const ratedBands = band_details_technical ?? band_details;

  // Total Final = sum of per-band finals (may exceed fses_technical when min is binding)
  const totalFinal =
    ratedBands?.reduce((s, b) => s + (b.final ?? 0), 0) ?? null;

  return (
    <Stack spacing={1.5}>
      {/* Per-band breakdown */}
      <SectionHeader
        label={"Rated Layer Bands"}
        open={bandOpen}
        onToggle={() => setBandOpen((v) => !v)}
      />
      <Collapse in={bandOpen} timeout={"auto"} unmountOnExit>
        <Table size={"small"} sx={{ mt: 0.5 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell sx={{ ...hSx("left"), width: 120 }}>
                {"Band Start"}
              </TableCell>
              <TableCell sx={hSx()} align={"right"}>
                {"Combined Prem"}
              </TableCell>
              <TableCell sx={hSx()} align={"right"}>
                {"Minimum"}
              </TableCell>
              <TableCell sx={hSx()} align={"right"}>
                {"Min Binding?"}
              </TableCell>
              <TableCell sx={hSx()} align={"right"}>
                {"Final Band Prem"}
              </TableCell>
              <TableCell sx={hSx()} align={"right"}>
                {"Min Uplift"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ratedBands?.map((b) => (
              <BandRow key={b.attachment_millions} band={b} />
            ))}
            {ratedBands?.length > 0 && (
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontSize: FONT, fontWeight: 700 }}>
                  {"Total"}
                </TableCell>
                <TableCell
                  sx={{ fontSize: FONT, fontWeight: 700 }}
                  align={"right"}
                >
                  {fmtAmt(totals?.fses_technical)}
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell
                  sx={{ fontSize: FONT, fontWeight: 700 }}
                  align={"right"}
                >
                  {fmtAmt(totalFinal)}
                </TableCell>
                <TableCell
                  sx={{ fontSize: FONT, fontWeight: 700 }}
                  align={"right"}
                >
                  {fmtAmt(
                    ratedBands.reduce((s, b) => s + (b.min_uplift || 0), 0)
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Collapse>

      {/* What If Layer Pricing — matches Excel layout */}
      {band_details?.length > 0 && (
        <>
          <SectionHeader
            label={"What If Layer Pricing"}
            open={whatIfOpen}
            onToggle={() => setWhatIfOpen((v) => !v)}
          />
          <Collapse in={whatIfOpen} timeout={"auto"} unmountOnExit>
            <Table size={"small"} sx={{ mt: 0.5 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell sx={{ ...hSx("left"), width: 100 }} />
                  <TableCell sx={hSx()} align={"right"}>
                    {"GL Premises"}
                  </TableCell>
                  <TableCell sx={hSx()} align={"right"}>
                    {"GL Products"}
                  </TableCell>
                  <TableCell sx={hSx()} align={"right"}>
                    {"Liquor"}
                  </TableCell>
                  <TableCell sx={hSx()} align={"right"}>
                    {"Auto"}
                  </TableCell>
                  <TableCell sx={hSx()} align={"right"}>
                    {"Total"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Individual base policy bands */}
                {band_details.map((b, i) => (
                  <WhatIfRow
                    key={b.attachment_millions}
                    label={i === 0 ? "1 x 1" : `1 x ${i} x P`}
                    prem_ops={b.prem_ops}
                    pco={b.pco}
                    liquor={b.liquor}
                    auto={b.auto}
                    combined={b.combined}
                  />
                ))}
                {/* Base policy total — Excel "5 x P" row uses override-tier sums
                    (= sum of the 1 x N x P rows above), matching FSES Guideline pre-IRPM. */}
                <WhatIfRow
                  label={`${band_details.length} x P`}
                  prem_ops={totals?.prem_ops}
                  pco={totals?.pco}
                  liquor={totals?.liquor}
                  auto={totals?.auto}
                  combined={
                    (totals?.prem_ops ?? 0) +
                    (totals?.pco ?? 0) +
                    (totals?.liquor ?? 0) +
                    (totals?.auto ?? 0)
                  }
                  bold
                  highlight
                />
                {/* Stacked tower scenarios above base */}
                {what_if?.map((s) => (
                  <WhatIfRow
                    key={s.label}
                    label={s.label}
                    prem_ops={s.prem_ops}
                    pco={s.pco}
                    liquor={s.liquor}
                    auto={s.auto}
                    combined={s.combined}
                  />
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </>
      )}
    </Stack>
  );
};

export default XsRatingDetails;
